import React from 'react';
import { useDispatch } from 'react-redux';
import { Accordion, Container } from 'react-bootstrap';
import uniq from 'lodash/uniq';

import SVButton from 'components/elements/SVButton';

import useMountEffect from 'hooks/useMountEffect';
import useCurrentApplication from 'hooks/useCurrentApplication';
import useChat from 'hooks/useChat';
import { getFlags } from 'store/actions/flags';
import {
  formType as FormTypes,
  programType,
  programSubType,
  prqNavigation,
  meetingsAndRoundsNavigation,
  keyFindingsNavigation,
} from 'helpers/forms';
import {
  getProgramTypeFromApplicationType,
  getSubTypeFromApplicationType,
} from 'utils/enums';

import CriteriaSection from './components/CriteriaSection';

export interface SurveyPage {
  name: string;
  [key: string]: any;
}

interface Page {
  name: string;
  title: string;
  questions: any[];
  getType?: () => string;
  isEmpty?: () => boolean;
}

interface Model {
  currentPage: Page;
  visiblePages: Page[];
}

export interface SurveyNavigationProps {
  surveyVersionId: number;
  model: Model;
  formType: string;
  onPageChanged: (page: SurveyPage) => void;
  onSubmit?: () => void;
}

const getSurveySections = ({
  formType,
  appData,
}: {
  formType: string;
  appData: any;
}): string[] => {
  const programTypeLabel = getProgramTypeFromApplicationType(appData?.applicationType);
  const programSubtypeLabel = getSubTypeFromApplicationType(appData?.applicationType);

  let programTypeId;
  if (programTypeLabel === 'Fellowship') {
    programTypeId = programType.FELLOWSHIP;
  } else if (programTypeLabel === 'Burn Center') {
    programTypeId = programType.BURNCENTER;
  }

  let programSubtypeId;
  switch (programSubtypeLabel) {
    case 'verification':
      programSubtypeId = programSubType.VERIFICATION;
      break;
    case 're verification':
      programSubtypeId = programSubType.RE_VERIFICATION;
      break;
    case 'provisional verification':
      programSubtypeId = programSubType.PROVISIONAL_VERIFICATION;
      break;
    case 'consultation only':
      programSubtypeId = programSubType.CONSULTATION;
      break;
    case 'focused review':
      programSubtypeId = programSubType.FOCUS_REVIEW;
      break;
    default:
      break;
  }

  switch (formType) {
    case FormTypes.PRQ:
      return prqNavigation({ programTypeId, programSubtypeId });
    case FormTypes.MeetingsAndRounds:
      return meetingsAndRoundsNavigation;
    case FormTypes.KeyFindings:
      return keyFindingsNavigation({ programTypeId });
    default:
      return [];
  }
};

const SurveyNavigation: React.FC<SurveyNavigationProps> = ({
  surveyVersionId,
  model,
  formType,
  onPageChanged,
  onSubmit,
}) => {
  const dispatch = useDispatch();
  const {
    appData, userTypeId, isReviewer, isVerification,
  } = useCurrentApplication();
  const { getCommentCount } = useChat();

  const [activeSectionKey, setActiveSectionKey] = React.useState<string>('0');

  const { visiblePages, currentPage } = model;

  useMountEffect(() => {
    if (!userTypeId) return;
    getCommentCount(appData.id, surveyVersionId, userTypeId);
    dispatch<any>(getFlags(appData.id, surveyVersionId, userTypeId));
  });

  React.useEffect(() => {
    setActiveSectionKey(currentPage.name.split('_')[0]);
  }, [currentPage]);

  const canSubmit = React.useMemo(() => {
    if (appData.status != null) return false;
    switch (formType) {
      case FormTypes.KeyFindings:
        return isReviewer && !appData.keyFindingsSubmittedAt;
      case FormTypes.MeetingsAndRounds: {
        if (isVerification) return !appData.meetingsAndRoundsSubmittedAt;
        if (isReviewer) return !appData.meetingsAndRoundsSrSubmittedAt;
        return false;
      }
      case FormTypes.PRQ: {
        if (isReviewer) return !appData.prqSrSubmittedAt;
        if (isVerification) return !appData.prqSubmittedAt;
        return false;
      }
      default:
        return false;
    }
  }, [appData, formType, isReviewer, isVerification]);

  const surveySections = React.useMemo(() => {
    const sections = getSurveySections({ formType, appData }).map(
      (label, id) => ({ id, label }),
    );

    const pageIds = uniq(visiblePages.map(({ name }) => Number(name.split('_')[0])));

    return sections.filter(({ id }) => pageIds.includes(id));
  }, [formType, appData, visiblePages]);

  return (
    <div style={{ minWidth: '300px', maxWidth: '300px' }}>
      <Accordion activeKey={activeSectionKey.toString()}>
        {surveySections.map(({ id, label }: any) => (
          <Accordion.Item eventKey={String(id)} key={id}>
            <Accordion.Header onClick={() => setActiveSectionKey(id)}>
              <div style={{ fontWeight: '700', color: 'black' }}>{label}</div>
            </Accordion.Header>
            <Accordion.Collapse eventKey={String(id)}>
              <CriteriaSection
                sectionId={id}
                model={model}
                onPageChanged={onPageChanged}
              />
            </Accordion.Collapse>
          </Accordion.Item>
        ))}
      </Accordion>
      {canSubmit && (
        <Container className="pt-3">
          <SVButton size="sm" onClick={onSubmit}>
            Final Submit
          </SVButton>
        </Container>
      )}
    </div>
  );
};

export default SurveyNavigation;
